// src/admin/OrderManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  ShoppingBag, Search, Eye, Trash2, X, CheckCircle,
  Clock, XCircle, Truck, MapPin, Phone, Mail, Package,
  CreditCard, ChevronLeft, ChevronRight, Calendar, Hash, ExternalLink
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';
import PostExOrderForm from './PostExOrderForm';
import { bookOrderOnPostEx } from '../utils/postexApi';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 20;
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // PostEx state
  const [showPostExForm, setShowPostExForm] = useState(false);
  const [selectedOrderForPostEx, setSelectedOrderForPostEx] = useState(null);
  const [bookingPostEx, setBookingPostEx] = useState(false);

  const statusConfig = {
    processing: { label: 'Processing', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
    'out-for-delivery': { label: 'Out for Delivery', color: 'bg-purple-100 text-purple-700', icon: Truck },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
    'on-hold': { label: 'On Hold', color: 'bg-gray-100 text-gray-700', icon: Clock },
    'not-complete': { label: 'Not Complete', color: 'bg-orange-100 text-orange-700', icon: XCircle },
    trash: { label: 'Trash', color: 'bg-gray-200 text-gray-500', icon: Trash2 },
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'orders'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setSelectAll(false);
    setCurrentPage(1);
  }, [statusFilter, searchTerm]);

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: new Date(),
      });
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateTrackingField = async (orderId, field, value) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { [field]: value });
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, [field]: value });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ Book Order on PostEx via API
  const handleBookOnPostEx = async (order) => {
    if (!window.confirm(`Book order #${order.id.slice(-6).toUpperCase()} on PostEx?`)) return;

    setBookingPostEx(true);
    try {
      const result = await bookOrderOnPostEx(order);

      if (result.success) {
        await updateDoc(doc(db, 'orders', order.id), {
          trackingNumber: result.trackingNumber || '',
          postexOrderId: result.orderId || '',
          postexBookedAt: new Date(),
          status: 'shipped',
          courierName: 'PostEx',
        });

        setSuccessMessage(`✅ Booked on PostEx! CN: ${result.trackingNumber}`);
        setShowSuccess(true);
        fetchOrders();

        if (selectedOrder && selectedOrder.id === order.id) {
          setSelectedOrder({
            ...selectedOrder,
            trackingNumber: result.trackingNumber,
            status: 'shipped',
            courierName: 'PostEx',
          });
        }
      } else {
        alert(`❌ PostEx Error: ${result.error}`);
      }
    } catch (error) {
      console.error(error);
      alert(`❌ Failed: ${error.message}`);
    } finally {
      setBookingPostEx(false);
    }
  };

  const togglePaymentStatus = async (orderId, currentPaymentStatus) => {
    const newStatus = currentPaymentStatus === 'paid' ? 'unpaid' : 'paid';
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        paymentStatus: newStatus,
        updatedAt: new Date(),
      });
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, paymentStatus: newStatus });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete order #${order.id?.slice(-6)}?`)) return;
    try {
      await deleteDoc(doc(db, 'orders', order.id));
      fetchOrders();
      if (selectedOrder?.id === order.id) {
        setShowDetailModal(false);
        setSelectedOrder(null);
      }
    } catch (error) {
      alert('Failed to delete order.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} orders?`)) return;
    try {
      for (const id of selectedIds) {
        await deleteDoc(doc(db, 'orders', id));
      }
      const count = selectedIds.length;
      setSelectedIds([]);
      setSelectAll(false);
      fetchOrders();
      setSuccessMessage(`${count} orders deleted!`);
      setShowSuccess(true);
    } catch (error) {
      alert('Failed to delete orders.');
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} orders ko "${newStatus}" kar dein?`)) return;
    try {
      for (const id of selectedIds) {
        await updateDoc(doc(db, 'orders', id), {
          status: newStatus,
          updatedAt: new Date(),
        });
      }
      const count = selectedIds.length;
      setSelectedIds([]);
      setSelectAll(false);
      fetchOrders();
      setSuccessMessage(`${count} orders updated!`);
      setShowSuccess(true);
    } catch (error) {
      alert('Failed to update orders.');
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const getFilteredOrders = () => {
    let filtered = [...orders];
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => (o.status || 'processing') === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o =>
        o.id?.toLowerCase().includes(term) ||
        o.customerName?.toLowerCase().includes(term) ||
        o.customerEmail?.toLowerCase().includes(term)
      );
    }
    return filtered;
  };

  const filteredOrders = getFilteredOrders();
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  const toggleSelectAll = () => {
    if (selectAll) setSelectedIds([]);
    else setSelectedIds(currentOrders.map(o => o.id));
    setSelectAll(!selectAll);
  };

  const counts = {
    all: orders.length,
    processing: orders.filter(o => (o.status || 'processing') === 'processing').length,
    completed: orders.filter(o => o.status === 'completed').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
    'on-hold': orders.filter(o => o.status === 'on-hold').length,
    'not-complete': orders.filter(o => o.status === 'not-complete').length,
    trash: orders.filter(o => o.status === 'trash').length,
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour(s) ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} day(s) ago`;
    return date.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.processing;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${config.color}`}>
        <Icon size={12} /> {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">

      <div>
        <h1 className="text-2xl font-bold text-gray-800">Orders Management</h1>
        <p className="text-gray-500 text-sm mt-1">{orders.length} total orders</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'processing', label: 'Processing' },
            { key: 'completed', label: 'Completed' },
            { key: 'cancelled', label: 'Cancelled' },
            { key: 'on-hold', label: 'On Hold' },
            { key: 'not-complete', label: 'Not Complete' },
            { key: 'trash', label: 'Trash' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === tab.key ? 'bg-green-700 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              {tab.label} ({counts[tab.key] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Search + Bulk */}
      <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
        <div className="relative">
          <input type="text" placeholder="Search by Order ID, customer name, ya email..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm" />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap p-3 rounded-lg anim-slideDown"
            style={{ backgroundColor: 'rgba(220, 130, 154, 0.1)', border: '1px dashed var(--color-accent)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-link)' }}>
              {selectedIds.length} orders selected
            </span>
            <div className="flex flex-wrap gap-2 ml-auto">
              <button onClick={() => handleBulkStatus('processing')}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
                Mark Processing
              </button>
              <button onClick={() => handleBulkStatus('shipped')}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 hover:bg-blue-200">
                Mark Shipped
              </button>
              <button onClick={() => handleBulkStatus('completed')}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-100 text-green-800 hover:bg-green-200">
                Mark Completed
              </button>
              <button onClick={handleBulkDelete}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-100 text-red-800 hover:bg-red-200 flex items-center gap-1">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={() => { setSelectedIds([]); setSelectAll(false); }}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-pink-400 border-t-transparent"></div>
            <p className="mt-4 text-gray-500">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <ShoppingBag size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No orders found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 w-10">
                      <input type="checkbox" checked={selectAll} onChange={toggleSelectAll}
                        className="w-4 h-4 accent-pink-500 cursor-pointer" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Order</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase hidden lg:table-cell">Payment</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Total</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentOrders.map((order) => (
                    <tr key={order.id} className={`hover:bg-gray-50 transition ${selectedIds.includes(order.id) ? 'bg-pink-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input type="checkbox" checked={selectedIds.includes(order.id)}
                          onChange={() => toggleSelectOne(order.id)}
                          className="w-4 h-4 accent-pink-500 cursor-pointer" />
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">#{order.id?.slice(-6).toUpperCase()}</p>
                          <p className="text-xs text-gray-500">{order.customerName || 'Unknown'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 hidden md:table-cell">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-4 py-4">{getStatusBadge(order.status || 'processing')}</td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <button onClick={() => togglePaymentStatus(order.id, order.paymentStatus)}
                          className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                            order.paymentStatus === 'pending-verification' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                          {order.paymentStatus === 'paid' ? 'Paid' :
                           order.paymentStatus === 'pending-verification' ? 'Pending' : 'Unpaid'}
                        </button>
                      </td>
                      <td className="px-4 py-4 text-right font-bold text-green-800 text-sm">
                        Rs. {order.total || 0}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => { setSelectedOrder(order); setShowDetailModal(true); }}
                            className="text-green-600 hover:bg-green-50 p-2 rounded-lg transition" title="View">
                            <Eye size={16} />
                          </button>
                          <button onClick={() => handleDelete(order)}
                            className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">
                  Showing {indexOfFirstOrder + 1}-{Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length}
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-white disabled:opacity-40 transition">
                    <ChevronLeft size={16} />
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                        className={`min-w-[32px] h-8 rounded-lg text-xs font-semibold transition ${
                          currentPage === pageNum ? 'bg-pink-500 text-white' : 'hover:bg-white text-gray-700'
                        }`}>
                        {pageNum}
                      </button>
                    );
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-white disabled:opacity-40 transition">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ================= DETAIL MODAL ================= */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Order #{selectedOrder.id?.slice(-6).toUpperCase()}</h2>
                <p className="text-xs text-gray-500">{formatDate(selectedOrder.createdAt)}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">

              {/* Status Update */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Package size={18} style={{ color: 'var(--color-link)' }} /> Update Order Status
                </h3>
                <div className="flex flex-wrap gap-2">
                  {['processing', 'confirmed', 'shipped', 'out-for-delivery', 'completed', 'cancelled', 'on-hold', 'not-complete'].map(s => (
                    <button key={s} disabled={updatingStatus}
                      onClick={() => updateOrderStatus(selectedOrder.id, s)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${
                        (selectedOrder.status || 'processing') === s
                          ? 'text-white shadow-md'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                      style={{
                        backgroundColor: (selectedOrder.status || 'processing') === s ? 'var(--color-button-bg)' : undefined,
                      }}>
                      {statusConfig[s]?.label || s}
                    </button>
                  ))}
                </div>
              </div>

              {/* PostEx Booking Section */}
              {selectedOrder.status === 'processing' && (
                <div className="border-2 rounded-xl p-4" style={{ borderColor: 'var(--color-button-bg)', backgroundColor: '#FFF2F6' }}>
                  <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
                    📦 PostEx Delivery
                  </h3>
                  <p className="text-xs mb-3" style={{ color: 'var(--color-secondary-text)' }}>
                    Order ko PostEx par book karein. Do tareeqay hain:
                  </p>

                  {/* API Method */}
                  <button
                    onClick={() => handleBookOnPostEx(selectedOrder)}
                    disabled={bookingPostEx}
                    className={`w-full py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 btn-2d text-white mb-2 ${
                      bookingPostEx ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    style={{ backgroundColor: 'var(--color-button-bg)' }}
                  >
                    {bookingPostEx ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full anim-spin"></span>
                        Booking...
                      </>
                    ) : (
                      <>
                        <ExternalLink size={16} /> Book via API (Auto)
                      </>
                    )}
                  </button>

                  {/* Manual Method */}
                  <button
                    onClick={() => {
                      setSelectedOrderForPostEx(selectedOrder);
                      setShowPostExForm(true);
                    }}
                    className="w-full py-2.5 rounded-lg font-semibold text-xs flex items-center justify-center gap-2 transition border-2"
                    style={{
                      borderColor: 'var(--color-button-bg)',
                      color: 'var(--color-link)',
                      backgroundColor: 'white',
                    }}
                  >
                    📋 Manual Booking (Copy Fields)
                  </button>
                </div>
              )}

              {/* Show PostEx info if booked */}
              {selectedOrder.postexBookedAt && (
                <div className="border border-green-200 bg-green-50 rounded-xl p-4">
                  <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                    ✅ PostEx Booked
                  </h3>
                  {selectedOrder.trackingNumber && (
                    <p className="text-xs text-green-700">
                      CN: <span className="font-mono font-bold">{selectedOrder.trackingNumber}</span>
                    </p>
                  )}
                  <p className="text-xs text-green-700">
                    Booked on: {formatDate(selectedOrder.postexBookedAt)}
                  </p>
                </div>
              )}

              {/* Tracking Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <Truck size={18} /> Tracking Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Est. Delivery Date</label>
                    <input
                      type="text"
                      value={selectedOrder.estimatedDelivery || ''}
                      onChange={(e) => setSelectedOrder({ ...selectedOrder, estimatedDelivery: e.target.value })}
                      onBlur={(e) => updateTrackingField(selectedOrder.id, 'estimatedDelivery', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none"
                      placeholder="e.g., 25 Oct 2026"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Tracking Number</label>
                    <input
                      type="text"
                      value={selectedOrder.trackingNumber || ''}
                      onChange={(e) => setSelectedOrder({ ...selectedOrder, trackingNumber: e.target.value })}
                      onBlur={(e) => updateTrackingField(selectedOrder.id, 'trackingNumber', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none"
                      placeholder="e.g., CN123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Courier</label>
                    <input
                      type="text"
                      value={selectedOrder.courierName || ''}
                      onChange={(e) => setSelectedOrder({ ...selectedOrder, courierName: e.target.value })}
                      onBlur={(e) => updateTrackingField(selectedOrder.id, 'courierName', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none"
                      placeholder="e.g., PostEx"
                    />
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} style={{ color: 'var(--color-link)' }} />
                  <span className="font-semibold text-gray-700">Payment:</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    selectedOrder.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                    selectedOrder.paymentStatus === 'pending-verification' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {selectedOrder.paymentStatus === 'paid' ? 'PAID' :
                     selectedOrder.paymentStatus === 'pending-verification' ? 'PENDING' : 'UNPAID'}
                  </span>
                </div>
                <button onClick={() => togglePaymentStatus(selectedOrder.id, selectedOrder.paymentStatus)}
                  className="text-xs font-semibold underline" style={{ color: 'var(--color-link)' }}>Change</button>
              </div>

              {/* Customer */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h3 className="font-bold text-gray-800 mb-3">Customer Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-gray-500">Name</p><p className="font-semibold">{selectedOrder.customerName || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Email</p><p className="font-semibold">{selectedOrder.customerEmail || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Phone</p><p className="font-semibold">{selectedOrder.phone || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">City</p><p className="font-semibold">{selectedOrder.city || '-'}</p></div>
                  <div className="md:col-span-2"><p className="text-xs text-gray-500">Address</p>
                    <p className="font-semibold">{selectedOrder.address || '-'}{selectedOrder.state && `, ${selectedOrder.state}`}{selectedOrder.postalCode && ` - ${selectedOrder.postalCode}`}</p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h3 className="font-bold text-gray-800 mb-3">Products Ordered</h3>
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        {item.image && <img src={item.image} alt={item.title} className="w-14 h-14 object-cover rounded-lg" />}
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{item.title}</p>
                          <p className="text-xs text-gray-500">Qty: {item.quantity} × Rs. {item.price}</p>
                        </div>
                        <p className="font-bold text-green-800 text-sm">Rs. {(item.price * item.quantity) || 0}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-gray-500">No items.</p>}
              </div>

              {/* Totals */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-semibold">Rs. {selectedOrder.subtotal || 0}</span></div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-green-700"><span>Discount {selectedOrder.couponCode && `(${selectedOrder.couponCode})`}</span><span className="font-semibold">- Rs. {selectedOrder.discount}</span></div>
                )}
                {selectedOrder.shipping > 0 && <div className="flex justify-between"><span className="text-gray-600">Shipping</span><span className="font-semibold">Rs. {selectedOrder.shipping}</span></div>}
                {selectedOrder.tax > 0 && <div className="flex justify-between"><span className="text-gray-600">{selectedOrder.taxName || 'Tax'}</span><span className="font-semibold">Rs. {selectedOrder.tax}</span></div>}
                <div className="flex justify-between border-t border-gray-200 pt-2 mt-2 text-lg">
                  <span className="font-bold">Total</span>
                  <span className="font-bold text-green-800">Rs. {selectedOrder.total || 0}</span>
                </div>
              </div>

              {/* Bank Transfer */}
              {selectedOrder.paymentMethod === 'bank' && (
                <div className="border border-yellow-200 bg-yellow-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-bold text-yellow-900">🏦 Bank Transfer Details</h3>
                  {selectedOrder.senderName && <div><p className="text-xs text-gray-600">Sender</p><p className="font-semibold text-sm">{selectedOrder.senderName}</p></div>}
                  {selectedOrder.transactionId && <div><p className="text-xs text-gray-600">Transaction ID</p><p className="font-semibold text-sm">{selectedOrder.transactionId}</p></div>}
                  {selectedOrder.paymentScreenshot && (
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Screenshot</p>
                      <img src={selectedOrder.paymentScreenshot} alt="Screenshot" className="w-full max-w-xs rounded-lg border" />
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* PostEx Form Modal */}
      {showPostExForm && selectedOrderForPostEx && (
        <PostExOrderForm
          order={selectedOrderForPostEx}
          onClose={() => {
            setShowPostExForm(false);
            setSelectedOrderForPostEx(null);
          }}
        />
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default OrderManagement;